import { CheckIcon, Edit2Icon, XIcon } from "lucide-react";
import React from "react";
import type { SerializedDecisionNode } from "@/components/Editor/nodes/types";
import { cn } from "@/lib/utils";
import { useLocale } from "@/utils/i18n";

interface DecisionViewProps {
  node: SerializedDecisionNode;
}

export const DecisionView: React.FC<DecisionViewProps> = ({ node }) => {
  const locale = useLocale();
  const {
    ticker,
    version,
    decision,
    actionType,
    price,
    quantity,
    reason,
    executedAt,
    createdAt,
    contractId,
    // Optional pre-analysis fields
    confidence,
    riskLevel,
    timeHorizon,
  } = node;

  // Format executedAt date
  const formatDateTime = (isoString?: string, locale?: string) => {
    if (!isoString) return "";
    try {
      const date = new Date(isoString);
      return date.toLocaleString(locale || "en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return isoString;
    }
  };

  // Get decision label
  const getDecisionLabel = (dec: string) => {
    switch (dec) {
      case "BUY":
        return "买入";
      case "SELL":
        return "卖出";
      case "HOLD":
        return "持有";
      default:
        return dec;
    }
  };

  const getDecisionColor = (dec: string) => {
    switch (dec) {
      case "BUY":
        return "text-green-600";
      case "SELL":
        return "text-red-600";
      case "HOLD":
        return "text-blue-600";
      default:
        return "text-muted-foreground";
    }
  };

  // Get action type badge
  const getActionTypeBadge = (action: string) => {
    switch (action) {
      case "FOLLOW":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-green-50 text-green-700 text-xs font-medium">
            <CheckIcon className="w-3 h-3" />
            遵守契约
          </span>
        );
      case "OVERRIDE":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-red-50 text-red-700 text-xs font-medium">
            <XIcon className="w-3 h-3" />
            违背契约
          </span>
        );
      case "NONE":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-gray-50 text-gray-700 text-xs font-medium">
            <Edit2Icon className="w-3 h-3" />
            独立决策
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-gray-50 text-gray-700 text-xs font-medium">
            {action}
          </span>
        );
    }
  };

  return (
    <div className="my-4 p-4 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg">
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-lg font-semibold text-blue-900 dark:text-blue-100">决策记录</span>
          <span className="inline-flex items-center px-2 py-0.5 rounded bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 text-xs font-medium">
            ${ticker}
          </span>
        </div>
        {contractId && <span className="text-xs text-muted-foreground">关联契约: {contractId.slice(0, 8)}...</span>}
      </div>

      {/* Decision Type */}
      <div className="mb-3">
        <div className={cn("text-xl font-bold mb-1", getDecisionColor(decision))}>{getDecisionLabel(decision)}</div>
        {getActionTypeBadge(actionType)}
      </div>

      {/* Execution Details */}
      <div className="grid grid-cols-2 gap-4 mb-3 text-sm">
        <div>
          <span className="text-muted-foreground">价格:</span> <span className="font-medium">${(price ?? 0).toFixed(2)}</span>
        </div>
        <div>
          <span className="text-muted-foreground">数量:</span> <span className="font-medium">{quantity ?? 0}</span>
        </div>
        {executedAt && (
          <div className="col-span-2">
            <span className="text-muted-foreground">执行时间:</span>{" "}
            <span className="font-medium">{formatDateTime(executedAt, locale)}</span>
          </div>
        )}
      </div>

      {/* Reason */}
      {reason && (
        <div className="mb-3">
          <div className="text-sm text-muted-foreground mb-1">原因:</div>
          <div className="text-sm bg-white dark:bg-gray-800 p-2 rounded border border-blue-100 dark:border-blue-900">{reason}</div>
        </div>
      )}

      {/* Optional Pre-Analysis Fields */}
      {(confidence || riskLevel || timeHorizon) && (
        <div className="mt-3 pt-3 border-t border-blue-200 dark:border-blue-800">
          <div className="text-xs text-muted-foreground mb-2">事前分析 (可选)</div>
          <div className="grid grid-cols-3 gap-2 text-xs">
            {confidence != null && (
              <div>
                <span className="text-muted-foreground">信心度:</span> <span className="font-medium">{confidence.toFixed(0)}%</span>
              </div>
            )}
            {riskLevel && (
              <div>
                <span className="text-muted-foreground">风险:</span>{" "}
                <span
                  className={cn(
                    "font-medium",
                    riskLevel === "LOW" && "text-green-600",
                    riskLevel === "MEDIUM" && "text-yellow-600",
                    riskLevel === "HIGH" && "text-red-600",
                  )}
                >
                  {riskLevel === "LOW" ? "低" : riskLevel === "HIGH" ? "高" : "中"}
                </span>
              </div>
            )}
            {timeHorizon && (
              <div>
                <span className="text-muted-foreground">期限:</span> <span className="font-medium">{timeHorizon}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Footer metadata */}
      <div className="mt-3 pt-2 border-t border-blue-200 dark:border-blue-800 text-xs text-muted-foreground">
        <div className="flex justify-between">
          {createdAt && <span>创建: {formatDateTime(createdAt, locale)}</span>}
          {version && <span>v{version}</span>}
        </div>
      </div>
    </div>
  );
};

export default DecisionView;
